import {
  Anchor,
  Box,
  Breadcrumbs,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowsLeftRight,
  IconBrain,
  IconChevronRight,
  IconFlask,
  IconHistory,
  IconSettings,
  IconUpload,
  IconWebhook,
} from '@tabler/icons';
import { Link, useNavigate } from 'react-router-dom';
import { StatusBadge } from '../../../components';
import { getIntegrationMeta } from '../../../components/integration-meta';
import { IntegrationSyncRunsTimeline } from '../../../components/integrations/components/IntegrationSyncRuns/IntegrationSyncRunsTimeline';
import { IntegrationPlaygroundTab } from '../../integration-playground';
import { useAccount } from '../../../queries/use-accounts';
import { useIntegrationInstallation } from '../../../queries/use-integration-installations';
import { ObjectMappingsPanel } from './object-mappings-panel';

const INSTALLATION_TABS = [
  'settings',
  'mappings',
  'data-out',
  'ml-actions',
  'sync-runs',
  'webhooks',
  'playground',
] as const;
type InstallationTab = (typeof INSTALLATION_TABS)[number];

function resolveDetailTab(value: string | undefined): InstallationTab {
  return (INSTALLATION_TABS as readonly string[]).includes(value ?? '')
    ? (value as InstallationTab)
    : 'settings';
}

interface IntegrationInstallationViewProps {
  accountId: string;
  installationId: string;
  detailTab?: string;
}

export function IntegrationInstallationView({
  accountId,
  installationId,
  detailTab,
}: IntegrationInstallationViewProps) {
  const navigate = useNavigate();
  const activeTab = resolveDetailTab(detailTab);
  const { data: account } = useAccount(accountId);
  const {
    data: installation,
    isLoading,
    isError,
    error,
  } = useIntegrationInstallation(installationId, accountId);

  const meta = getIntegrationMeta(installation?.key);
  const Icon = meta.icon;

  const handleTabChange = (value: string) => {
    navigate(`/account/${accountId}/integration/${installationId}/${value}`);
  };

  return (
    <Stack spacing="xl">
      <Breadcrumbs separator={<IconChevronRight size={12} />}>
        <Anchor component={Link} to="/accounts" color="dimmed" size="sm">
          Accounts
        </Anchor>
        <Anchor component={Link} to={`/account/${accountId}`} color="dimmed" size="sm">
          {account?.name ?? accountId}
        </Anchor>
        <Anchor component={Link} to={`/account/${accountId}/integrations`} color="dimmed" size="sm">
          Integrations
        </Anchor>
        <Text size="sm">{installation ? meta.label : installationId}</Text>
      </Breadcrumbs>

      {installation ? (
        <>
          <Box>
            <Group position="apart" align="flex-start" noWrap>
              <Group spacing="md" align="center">
                <ThemeIcon size={42} radius="md" variant="light" color="bagel">
                  <Icon size={22} />
                </ThemeIcon>
                <Box>
                  <Group spacing="sm" align="center">
                    <Title order={2}>{meta.label}</Title>
                    <StatusBadge status={installation.status} />
                  </Group>
                  <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
                    {installation._id}
                  </Text>
                </Box>
              </Group>
            </Group>
          </Box>

          <Tabs
            value={activeTab}
            onTabChange={(value) => value && handleTabChange(value)}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value="settings" icon={<IconSettings size={16} />}>
                Settings
              </Tabs.Tab>
              <Tabs.Tab value="mappings" icon={<IconArrowsLeftRight size={16} />}>
                Object Mappings
              </Tabs.Tab>
              <Tabs.Tab value="data-out" icon={<IconUpload size={16} />}>
                Data Out
              </Tabs.Tab>
              <Tabs.Tab value="ml-actions" icon={<IconBrain size={16} />}>
                ML Actions
              </Tabs.Tab>
              <Tabs.Tab value="sync-runs" icon={<IconHistory size={16} />}>
                Task Runs
              </Tabs.Tab>
              <Tabs.Tab value="webhooks" icon={<IconWebhook size={16} />}>
                Webhooks
              </Tabs.Tab>
              <Tabs.Tab value="playground" icon={<IconFlask size={16} />}>
                Playground
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="settings" pt="md">
              <Paper withBorder p="lg">
                <Text size="sm" color="dimmed">
                  Settings will appear here.
                </Text>
              </Paper>
            </Tabs.Panel>
            <Tabs.Panel value="mappings" pt="md">
              <ObjectMappingsPanel
                accountId={installation.accountId}
                integrationKey={installation.key}
              />
            </Tabs.Panel>
            <Tabs.Panel value="data-out" pt="md">
              <Paper withBorder p="lg">
                <Text size="sm" color="dimmed">
                  Data out will appear here.
                </Text>
              </Paper>
            </Tabs.Panel>
            <Tabs.Panel value="ml-actions" pt="md">
              <Paper withBorder p="lg">
                <Text size="sm" color="dimmed">
                  ML actions will appear here.
                </Text>
              </Paper>
            </Tabs.Panel>
            <Tabs.Panel value="sync-runs" pt="md">
              <IntegrationSyncRunsTimeline
                accountId={installation.accountId}
                integrationKey={installation.key}
              />
            </Tabs.Panel>
            <Tabs.Panel value="webhooks" pt="md">
              <Paper withBorder p="lg">
                <Text size="sm" color="dimmed">
                  Webhooks will appear here.
                </Text>
              </Paper>
            </Tabs.Panel>
            <Tabs.Panel value="playground" pt="md">
              <IntegrationPlaygroundTab
                accountId={installation.accountId}
                integrationKey={installation.key}
              />
            </Tabs.Panel>
          </Tabs>
        </>
      ) : isLoading ? (
        <Center mih={200}>
          <Loader />
        </Center>
      ) : isError ? (
        <Text color="red">
          Failed to load installation: {(error as Error)?.message ?? 'Unknown error'}
        </Text>
      ) : (
        <Text color="dimmed">Installation not found.</Text>
      )}
    </Stack>
  );
}
